// Baby Stage view — opened by tapping the baby's name in the Header.
// Static, age-aware "what's happening / what to focus on" copy (instant + offline).

import type { EventRow } from "../lib/events";
import { gramsToLbOz, lastWeight } from "../lib/events";
import type { AgeBand } from "../data/tcbSchedule";
import type { AgeParts } from "../lib/age";
import { formatAge } from "../lib/age";
import { STAGE_COPY } from "../data/stageCopy";
import { useLocale } from "../lib/LocaleContext";

type Props = {
  open: boolean;
  babyName: string;
  band: AgeBand;
  age: AgeParts;
  events: EventRow[];
  onClose: () => void;
};

export function StageSheet({ open, babyName, band, age, events, onClose }: Props) {
  const { locale, t } = useLocale();

  if (!open) return null;

  const copy = STAGE_COPY[locale][band.phase];
  const weight = lastWeight(events);
  const ageLabel = formatAge(age);

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div style={styles.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={styles.handle} />

        <div style={styles.head}>
          <div style={styles.headLabel}>{t.stage.about}</div>
          <div className="bh-id-name" style={styles.headName}>
            {babyName}
          </div>
          <div style={styles.headline}>{copy.headline}</div>
        </div>

        <div style={styles.statGrid}>
          <Stat label={t.stage.age} value={ageLabel} />
          <Stat label={t.stage.weight} value={weight?.weight_grams != null ? formatLbOz(weight.weight_grams) : "—"} />
          <Stat label={t.stage.stage} value={t.phaseLabel[band.phase]} />
          <Stat label={t.stage.wakeWindow} value={`${band.wakeWindowMin}–${band.wakeWindowMax}m`} />
        </div>

        <div style={styles.sectionLabel}>{t.stage.whatsHappening}</div>
        <div style={styles.factList}>
          {copy.whatsHappening.map((fact, i) => (
            <div key={i} style={styles.factRow}>
              <span style={styles.factDot} />
              <span style={styles.factText}>{fact}</span>
            </div>
          ))}
        </div>

        <div style={styles.sectionLabel}>{t.stage.whatToFocus}</div>
        <div style={styles.focusCard}>{copy.focusNow}</div>

        <div style={styles.actions}>
          <button style={styles.ghost} onClick={onClose}>
            {t.stage.close}
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div style={styles.stat}>
      <div style={styles.statLabel}>{label}</div>
      <div className="bh-num" style={styles.statValue}>
        {value}
      </div>
    </div>
  );
}

function formatLbOz(grams: number): string {
  const { lb, oz } = gramsToLbOz(grams);
  return `${lb} lb ${oz} oz`;
}

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "oklch(0 0 0 / 0.55)",
    zIndex: 100,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
  },
  sheet: {
    background: "var(--bh-surface)",
    width: "100%",
    maxWidth: 520,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    padding: "10px 16px max(28px, env(safe-area-inset-bottom))",
    boxShadow: "0 -20px 60px oklch(0 0 0 / 0.4)",
    maxHeight: "92vh",
    overflowY: "auto",
  },
  handle: {
    width: 40,
    height: 4,
    background: "var(--bh-divider)",
    borderRadius: 2,
    margin: "0 auto 14px",
  },
  head: { padding: "0 4px 16px" },
  headLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "var(--bh-ink-3)",
  },
  headName: {
    fontFamily: "var(--bh-font-serif)",
    fontSize: 32,
    fontWeight: 500,
    letterSpacing: "-0.02em",
    lineHeight: 1.05,
    marginTop: 4,
    color: "var(--bh-ink)",
  },
  headline: { fontSize: 15, color: "var(--bh-ink-3)", marginTop: 6 },
  statGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 8,
    marginBottom: 16,
  },
  stat: {
    background: "var(--bh-surface-2)",
    borderRadius: 14,
    padding: "10px 12px",
  },
  statLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "var(--bh-ink-3)",
  },
  statValue: { fontSize: 16, fontWeight: 600, color: "var(--bh-ink)", marginTop: 3 },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "var(--bh-ink-3)",
    marginBottom: 8,
  },
  factList: { display: "flex", flexDirection: "column", gap: 10, marginBottom: 18 },
  factRow: { display: "flex", gap: 10, alignItems: "flex-start" },
  factDot: {
    width: 6,
    height: 6,
    borderRadius: 999,
    background: "var(--bh-feed)",
    marginTop: 8,
    flexShrink: 0,
  },
  factText: { fontSize: 15, lineHeight: 1.5, color: "var(--bh-ink-2)" },
  focusCard: {
    background: "var(--bh-surface-2)",
    borderRadius: 16,
    padding: 14,
    fontSize: 15,
    lineHeight: 1.5,
    color: "var(--bh-ink-2)",
    marginBottom: 16,
  },
  actions: { display: "flex", gap: 8 },
  ghost: {
    flex: 1,
    background: "var(--bh-surface-2)",
    color: "var(--bh-ink-2)",
    border: "none",
    padding: "14px",
    fontSize: 16,
    fontWeight: 500,
    borderRadius: 14,
    cursor: "pointer",
    minHeight: 56,
  },
};
